import { Box, Button, Stack, Typography } from '@mui/material';
import { HealthCenter, sortCleaningMissions } from '@silbo/core';
import { colors } from '@silbo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext';
import useLiveQuery from '../../../hooks/useLiveQuery';
import CleaningMissionCard from '../components/CleaningMissionCard';
import CleaningMissionsToolbar from '../components/CleaningMissionsToolbar';
import CleaningPage from '../components/CleaningPage';
import CurrentCleaningMissionBanner from '../components/CurrentCleaningMissionBanner';
import EmptyState from '../components/EmptyState';
import { useCleaningPreferences } from '../hooks/useCleaningPreferences';
import faceStarEyes from '../assets/images/face-star-eyes.svg';
import { useNow } from '../hooks/useNow';
import {
  getHealthCenterUnitsQueryOptions,
  getOpenCleaningMissionsQuery,
  getOpenCleaningMissionsQueryOptions,
} from '../queries';
import { CleaningMissionListItem, isInUnits, toCleaningMissionListItem } from '../utils/cleaning-mission-list-item';
import { unitOfBedFrom } from '../utils/unit-of-bed';

type CleaningMissionsPageProps = {
  healthCenter: HealthCenter;
};

const CleaningMissionsPage = ({ healthCenter }: CleaningMissionsPageProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const { user } = useAuthenticationContext();
  const { data: missions } = useSuspenseQuery(getOpenCleaningMissionsQueryOptions(healthCenter));
  const { data: units } = useSuspenseQuery(getHealthCenterUnitsQueryOptions(healthCenter));
  useLiveQuery(getOpenCleaningMissionsQuery(healthCenter));

  const { sortOrder, setSortOrder, unitIds, setUnitIds } = useCleaningPreferences();
  const [showsHidden, setShowsHidden] = useState(false);
  const now = useNow();

  const unitOfBed = unitOfBedFrom(units);
  const items = missions.map((mission) => toCleaningMissionListItem(mission, unitOfBed));
  const isMineInProgress = (item: CleaningMissionListItem) =>
    item.mission.status === 'in-progress' && !!user && item.mission.cleaner?.id === user.id;
  // Ma mission en cours reste visible quel que soit le filtre : c'est celle sur laquelle je travaille.
  const current = items.filter(isMineInProgress);
  const sorted = sortCleaningMissions(
    items.filter((item) => !isMineInProgress(item)),
    sortOrder
  );
  // Une unité mémorisée puis devenue inactive ne compte plus : l'agent ne pourrait pas la décocher.
  const selectedUnitIds = unitIds.filter((id) => units.some((unit) => unit.id === id));
  const hasUnitFilter = selectedUnitIds.length > 0;
  const shown = hasUnitFilter ? sorted.filter((item) => isInUnits(item, selectedUnitIds)) : sorted;
  const hidden = hasUnitFilter ? sorted.filter((item) => !isInUnits(item, selectedUnitIds)) : [];
  const hasHidden = hidden.length > 0;
  const isEmpty = shown.length === 0;
  const countLabel = hasUnitFilter
    ? t('missions.countFiltered', { count: shown.length, total: sorted.length })
    : t('missions.count', { count: shown.length });

  return (
    <CleaningPage
      title={t('missions.title')}
      toolbar={
        <CleaningMissionsToolbar
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          units={units}
          selectedUnitIds={selectedUnitIds}
          onSelectedUnitIdsChange={setUnitIds}
        />
      }
    >
      {current.length > 0 && (
        <Stack
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'common.white',
            paddingTop: '10px',
            paddingX: 3,
            paddingBottom: 1,
          }}
        >
          {current.map((item) => (
            <CurrentCleaningMissionBanner key={item.mission.id} mission={item.mission} unit={item.unit} now={now} />
          ))}
        </Stack>
      )}
      <Box sx={{ paddingTop: 4, paddingX: 4, paddingBottom: 2, borderBottom: `1px solid ${colors.grey[10]}` }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {countLabel}
        </Typography>
      </Box>
      {isEmpty && !hasUnitFilter && current.length === 0 && (
        <EmptyState
          image={faceStarEyes}
          title={t('missions.allClean.title')}
          message={t('missions.allClean.message')}
        />
      )}
      {isEmpty && (hasUnitFilter || current.length > 0) && (
        <Typography color="text.secondary" padding={4}>
          {hasUnitFilter ? t('missions.emptyInSelectedUnits') : t('missions.empty')}
        </Typography>
      )}
      {shown.map((item) => (
        <CleaningMissionCard key={item.mission.id} mission={item.mission} unit={item.unit} now={now} />
      ))}
      {hasHidden && (
        <Stack spacing={3} paddingY={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" paddingX={4}>
            <Typography color="text.secondary">{t('missions.hiddenByFilter', { count: hidden.length })}</Typography>
            <Button variant="text" size="small" onClick={() => setShowsHidden(!showsHidden)}>
              {showsHidden ? t('missions.hideHidden') : t('missions.showHidden')}
            </Button>
          </Stack>
          {showsHidden && (
            <Box>
              <Typography
                fontWeight={600}
                paddingX={4}
                paddingBottom={2}
                sx={{ borderBottom: `1px solid ${colors.grey[10]}` }}
              >
                {t('missions.otherUnits')}
              </Typography>
              {hidden.map((item) => (
                <CleaningMissionCard key={item.mission.id} mission={item.mission} unit={item.unit} now={now} />
              ))}
            </Box>
          )}
        </Stack>
      )}
    </CleaningPage>
  );
};

export default CleaningMissionsPage;
